import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminApi } from './useAdminApi';

const BASE = '/api/admin/shops';

// Data + actions for the Shops page: review queue, shops, ads moderation, ad packages and
// specialities. Each action refreshes the tab badges (summary) after it succeeds.
export const useShops = () => {
    const navigate = useNavigate();
    const { request, notify, hasAccess, user } = useAdminApi();

    const [summary, setSummary] = useState(null);
    const [specialities, setSpecialities] = useState([]);
    const [cities, setCities] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!user) navigate('/login');
        else if (!hasAccess('shops')) navigate('/');
    }, [user, hasAccess, navigate]);

    const loadSummary = useCallback(async () => {
        const json = await request(`${BASE}/summary`, { silent: true });
        if (json) setSummary(json);
    }, [request]);

    const loadSpecialities = useCallback(async () => {
        const json = await request(`${BASE}/specialities`);
        if (json) setSpecialities(json);
    }, [request]);

    // Public cities list, fetched without the admin token so a 401 can never log the admin out.
    const loadCities = useCallback(async () => {
        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/user/locations`);
            const json = await res.json();
            if (res.ok && json?.locations) setCities(json.locations.map((l) => l.city));
        } catch (_) {
            // cities only feed the filter / editor suggestions
        }
    }, []);

    // Load once per login, like the Trips page - keyed on the token, not on loader identities.
    useEffect(() => {
        if (user?.token && hasAccess('shops')) {
            loadSummary();
            loadSpecialities();
            loadCities();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.token]);

    // Runs a mutating request, shows a success message and refreshes the badges.
    const mutate = useCallback(async (path, { method = 'POST', body, success }) => {
        setBusy(true);
        const json = await request(path, { method, body });
        setBusy(false);
        if (json) {
            if (success) notify(success);
            loadSummary();
        }
        return json;
    }, [request, notify, loadSummary]);

    // ----- Shops -----
    const listShops = useCallback((query) => request(BASE, { query }), [request]);
    const getShop = useCallback((id) => request(`${BASE}/${id}`), [request]);
    const approveShop = (id, body) => mutate(`${BASE}/${id}/approve`, { body, success: 'تم اعتماد الورشة' });
    const rejectShop = (id, reason) => mutate(`${BASE}/${id}/reject`, { body: { reason }, success: 'تم رفض الورشة' });
    const suspendShop = (id, reason) => mutate(`${BASE}/${id}/suspend`, { body: { reason }, success: 'تم إيقاف الورشة' });
    const reinstateShop = (id) => mutate(`${BASE}/${id}/reinstate`, { success: 'تمت إعادة تفعيل الورشة' });
    const updateShop = (id, body) => mutate(`${BASE}/${id}`, { method: 'PATCH', body, success: 'تم حفظ التعديلات' });
    const deleteShop = (id) => mutate(`${BASE}/${id}`, { method: 'DELETE', success: 'تم حذف الورشة' });
    const approvePendingUpdate = (id, body) => mutate(`${BASE}/${id}/pending-update/approve`, { body, success: 'تم اعتماد التعديلات' });
    const rejectPendingUpdate = (id, reason) => mutate(`${BASE}/${id}/pending-update/reject`, { body: { reason }, success: 'تم رفض التعديلات' });

    // ----- Ads -----
    const listAds = useCallback((query) => request(`${BASE}/ads`, { query }), [request]);
    // body: { notification: { enabled, title, body, sendAt|null } }
    const approveAd = (id, body) => mutate(`${BASE}/ads/${id}/approve`, { body, success: 'تم تفعيل الإعلان' });
    const rejectAd = (id, reason) => mutate(`${BASE}/ads/${id}/reject`, { body: { reason }, success: 'تم رفض الإعلان وإعادة المبلغ' });
    const takeDownAd = (id, reason, refund) => mutate(`${BASE}/ads/${id}/take-down`, { body: { reason, refund }, success: 'تم إيقاف الإعلان' });

    // ----- Spots per limited form + bookings -----
    const getAdCapacity = useCallback(() => request(`${BASE}/ad-capacity`), [request]);
    const saveAdCapacity = (body) => mutate(`${BASE}/ad-capacity`, { method: 'PUT', body, success: 'تم حفظ عدد المساحات' });
    const listBookings = useCallback(() => request(`${BASE}/ad-bookings`), [request]);

    // ----- Ratings -----
    const listShopRatings = useCallback((id) => request(`${BASE}/${id}/ratings`), [request]);
    const updateRating = (ratingId, body) => mutate(`${BASE}/ratings/${ratingId}`, { method: 'PATCH', body, success: 'تم تعديل التقييم' });
    const deleteRating = (ratingId) => mutate(`${BASE}/ratings/${ratingId}`, { method: 'DELETE', success: 'تم حذف التقييم' });
    const setRatingOverride = (id, override) => mutate(`${BASE}/${id}/rating-override`, {
        method: 'PUT',
        body: { override },
        success: override === null ? 'تم إلغاء التقييم اليدوي' : 'تم تعيين التقييم',
    });

    // ----- Packages -----
    const listPackages = useCallback(() => request(`${BASE}/ad-packages`), [request]);
    const savePackage = (id, body) => mutate(id ? `${BASE}/ad-packages/${id}` : `${BASE}/ad-packages`, {
        method: id ? 'PATCH' : 'POST',
        body,
        success: id ? 'تم تحديث الباقة' : 'تمت إضافة الباقة',
    });
    const deletePackage = (id) => mutate(`${BASE}/ad-packages/${id}`, { method: 'DELETE', success: 'تم حذف الباقة' });

    // ----- Specialities -----
    const saveSpeciality = async (id, body) => {
        const json = await mutate(id ? `${BASE}/specialities/${id}` : `${BASE}/specialities`, {
            method: id ? 'PATCH' : 'POST',
            body,
            success: id ? 'تم تحديث التخصص' : 'تمت إضافة التخصص',
        });
        if (json) loadSpecialities();
        return json;
    };
    const deleteSpeciality = async (id) => {
        const json = await mutate(`${BASE}/specialities/${id}`, { method: 'DELETE', success: 'تم حذف التخصص' });
        if (json) loadSpecialities();
        return json;
    };

    return {
        summary,
        specialities,
        cities,
        busy,
        loadSummary,
        loadSpecialities,
        listShops,
        getShop,
        approveShop,
        rejectShop,
        suspendShop,
        reinstateShop,
        updateShop,
        deleteShop,
        approvePendingUpdate,
        rejectPendingUpdate,
        listAds,
        approveAd,
        rejectAd,
        takeDownAd,
        getAdCapacity,
        saveAdCapacity,
        listBookings,
        listShopRatings,
        updateRating,
        deleteRating,
        setRatingOverride,
        listPackages,
        savePackage,
        deletePackage,
        saveSpeciality,
        deleteSpeciality,
    };
};

export default useShops;
